import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { RESTAURANT, IMG } from "@/data/restaurant";
import { Phone, Mail, MapPin, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "General enquiry", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent — we'll be in touch.");
    } catch (err) {
      toast.error("Could not send. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24 bg-[var(--infuse-cream)] min-h-screen">
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <span className="gold-divider font-eyebrow text-[var(--infuse-gold)]">Contact</span>
          <h1 className="font-display text-6xl md:text-7xl text-[var(--infuse-emerald)] mt-5 leading-[0.95] max-w-3xl">
            Say hello — <em className="not-italic text-[var(--infuse-pomegranate)]">we'd love to host you.</em>
          </h1>
          <p className="mt-6 text-[var(--infuse-ink)]/70 text-lg max-w-2xl">
            Private dining, large parties, press, or just a question — we read every message.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-16 grid lg:grid-cols-12 gap-12">
        {/* Form */}
        <Reveal className="lg:col-span-7">
          <form data-testid="contact-form" onSubmit={submit} className="bg-white p-8 md:p-12 border border-[var(--infuse-gold)]/30">
            {sent ? (
              <div data-testid="contact-success" className="text-center py-10">
                <span className="gold-divider font-eyebrow text-[var(--infuse-gold)]">Received</span>
                <h2 className="font-display text-4xl mt-4 text-[var(--infuse-emerald)]">Thank you.</h2>
                <p className="mt-3 text-[var(--infuse-ink)]/70">We'll be in touch within 1 business day.</p>
                <button type="button" onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "General enquiry", message: "" }); }} className="btn-ghost-gold mt-8" data-testid="contact-new">
                  Send Another
                </button>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Your name" required>
                    <input data-testid="contact-name" required type="text" value={form.name} onChange={(e) => update("name", e.target.value)} className="infuse-input" />
                  </Field>
                  <Field label="Email" required>
                    <input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="infuse-input" />
                  </Field>
                </div>
                <div className="mt-5">
                  <Field label="Subject">
                    <select data-testid="contact-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} className="infuse-input">
                      <option>General enquiry</option>
                      <option>Private dining / events</option>
                      <option>Large party booking</option>
                      <option>Press & media</option>
                      <option>Careers</option>
                    </select>
                  </Field>
                </div>
                <div className="mt-5">
                  <Field label="Message" required>
                    <textarea data-testid="contact-message" rows={6} required value={form.message} onChange={(e) => update("message", e.target.value)} className="infuse-input resize-none" />
                  </Field>
                </div>
                <button type="submit" disabled={submitting} data-testid="contact-submit" className="btn-primary mt-8 disabled:opacity-60">
                  {submitting ? "Sending…" : "Send Message"} <ChevronRight size={16} />
                </button>
              </>
            )}
          </form>
        </Reveal>

        <Reveal className="lg:col-span-5" delay={120}>
          <div className="bg-[var(--infuse-emerald)] text-white p-10 relative overflow-hidden">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${IMG.emeraldBg})`, backgroundSize: "cover" }} />
            <div className="relative space-y-5">
              <h3 className="font-display text-3xl">Visit Infuse</h3>
              <p className="flex items-start gap-3 text-white/85"><MapPin size={18} className="mt-0.5 text-[var(--infuse-gold-soft)]" /> {RESTAURANT.address}</p>
              <p className="flex items-center gap-3 text-white/85"><Phone size={18} className="text-[var(--infuse-gold-soft)]" /> <a href={RESTAURANT.phoneHref}>{RESTAURANT.phone}</a></p>
              <p className="flex items-center gap-3 text-white/85"><Mail size={18} className="text-[var(--infuse-gold-soft)]" /> <a href={`mailto:${RESTAURANT.email}`}>{RESTAURANT.email}</a></p>
            </div>
          </div>
          <div className="mt-6 aspect-[4/3] border border-[var(--infuse-gold)]/30 overflow-hidden">
            <iframe title="Map" src={RESTAURANT.mapsEmbed} className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </Reveal>
      </section>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="font-eyebrow text-[0.62rem] text-[var(--infuse-ink)]/60 block mb-1.5">
        {label}{required && <span className="text-[var(--infuse-pomegranate)]"> *</span>}
      </span>
      {children}
    </label>
  );
}
